import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material'
import { VictoryChart, VictoryLine, VictoryBar, VictoryAxis, VictoryTooltip, VictoryVoronoiContainer } from 'victory'
import { DateRangePicker } from 'react-dates'
import 'react-dates/lib/css/_datepicker.css'
import 'react-dates/initialize'
import moment from 'moment'
import API from '../../utils/API'
import { useAuth } from '../../utils/authContext'
import StripeSignUp from '../../pages/ShowRunner/StripeSignUp'
import utils from '../../utils/utils'

const flashyTheme = {
  axis: {
    style: {
      axis: {
        stroke: "white",
        strokeWidth: 2,
      },
      axisLabel: {
        fontSize: 20,
        padding: 30,
        fill: "#0029FF",
      },
      grid: {
        stroke: "none",
      },
      ticks: {
        stroke: "transparent",
        size: 5,
        strokeWidth: 2,
      },
      tickLabels: {
        fontSize: 15,
        padding: 5,
        fill: "#A2A2A2",
      },
    },
  },
  line: {
    style: {
      data: {
        strokeWidth: 3,
        strokeLinecap: "round",
      },
      labels: {
        fontSize: 15,
        fill: "#0029FF",
      },
    },
  },
}

export default function Financial() {
      const { showrunner, loading: authLoading, auth } = useAuth()
      const [loading, setLoading] = useState(true)
      const [financialData, setFinancialData] = useState(null)
      const [stripeDone, setStripeDone] = useState(false)
      const [stripeAcctLink, setStripeAcctLink] = useState(false)
      const [startDate, setStartDate] = useState(moment().subtract(1, 'week'))
      const [endDate, setEndDate] = useState(moment())
      const [focusedInput, setFocusedInput] = useState(null)
      const [allTime, setAllTime] = useState(false)
      const [salesChart, setSalesChart] = useState([])
      const [ticketsChart, setTicketsChart] = useState([])
      
      useEffect(() => {
        if (authLoading) return
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        
        // Check Stripe setup status
        if (showrunner.stripe_connect_id) {
          API.getStripeConnectDash(showrunner.stripe_connect_id)
            .then(res => {
              const { link, error, onboarding } = res.data
              if (link) {
                setStripeAcctLink(link)
                setStripeDone(onboarding.status === 'complete')
              } else if (error) {
                setStripeDone(false)
                console.error('Stripe connect error', error)
              }
            })
            .catch(e => {
              console.error('Error getting stripe connect dash', e)
            })
        } else {
          setStripeDone(false)
        }
        
        fetchFinancialData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [showrunner, authLoading, startDate, endDate, allTime])
      
      const fetchFinancialData = async () => {
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        
        try {
          setLoading(true)
          
          const showrunnerId = showrunner.id
          const filters = {}
          
          // Only add date filters if not viewing all time
          if (startDate && endDate && !allTime) {
            filters.start_date = startDate.format('YYYY-MM-DD')
            filters.end_date = endDate.format('YYYY-MM-DD')
          }
          
          // Fetch financial data with date filters
          const financialResponse = await API.getFinancialData(showrunnerId, filters)
          setFinancialData(financialResponse.data)
          
          // Transform chart data for Victory
          const salesChartData = financialResponse.data.financial_chart.map(item => ({
            x: new Date(item.date_created__date),
            y: item.total_revenue
          }))
          setSalesChart(salesChartData)
          
          const ticketsChartData = financialResponse.data.ticket_chart.map(item => ({
            x: new Date(item.order__date_created__date),
            y: item.tickets_sold
          }))
          setTicketsChart(ticketsChartData)
          
        } catch (error) {
          console.error('Error fetching financial data:', error)
        } finally {
          setLoading(false)
        }
      }
  
      if (loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        )
      }
  
  // If Stripe is not set up, show the setup component
  if (!stripeDone) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Financial Dashboard
          </Typography>
          <Typography color="grey.600">
            Connect your Stripe account to start accepting payments and view your financial data
          </Typography>
        </Box>
        <Card>
          <CardContent>
            <StripeSignUp 
              showrunner={showrunner} 
              userID={auth?.id} 
              incomplete={showrunner?.stripe_connect_id && !stripeDone}
            />
          </CardContent>
        </Card>
      </Box>
    )
  }
  
  return (
    <Box>
      {/* Header with Go to Stripe Button */}
      <Box sx={{ 
        mb: 3, 
        p: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: 'linear-gradient(135deg, #E8F5E8 0%, #FFF8E8 100%)',
        borderRadius: 3,
        border: '2px solid #00C851',
        boxShadow: '0 4px 12px rgba(0, 200, 81, 0.1)',
      }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: 'success.main' }}>
            💰 Finances
          </Typography>
          <Typography color="grey.700" fontWeight="500">
            Track your revenue and ticket sales
          </Typography>
        </Box>
        {stripeAcctLink && (
          <Button
            variant="contained"
            href={stripeAcctLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #001ECC 0%, #0029FF 100%)',
              },
            }}
          >
            Go to Stripe
          </Button>
        )}
      </Box>
      
      {/* Date Range Picker */}
      <Card sx={{ 
        mb: 3,
        background: 'linear-gradient(135deg, #FFF8E8 0%, #ffffff 100%)',
        border: '2px solid #FFBB33',
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {allTime ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" fontWeight="600" color="grey.700">📅 Viewing: All Time</Typography>
                <Button 
                  size="small" 
                  variant="contained" 
                  onClick={() => setAllTime(false)}
                  sx={{
                    background: 'linear-gradient(135deg, #FFBB33 0%, #FFC107 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF9800 0%, #FFBB33 100%)',
                    },
                  }}
                >
                  Select Date Range
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="body2" fontWeight="600" color="grey.700">📅 Date Range:</Typography>
                <Box sx={{ position: 'relative', zIndex: 9999 }}>
                  <DateRangePicker
                    startDate={startDate}
                    startDateId="start_date_id"
                    endDate={endDate}
                    endDateId="end_date_id"
                    onDatesChange={({ startDate, endDate }) => {
                      setStartDate(startDate)
                      setEndDate(endDate)
                    }}
                    focusedInput={focusedInput}
                    onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                    numberOfMonths={1}
                    isOutsideRange={() => false}
                    displayFormat="MM/DD/YYYY"
                    small
                  />
                </Box>
                <Button 
                  size="small" 
                  variant="contained" 
                  onClick={() => setAllTime(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #001ECC 0%, #0029FF 100%)',
                    },
                  }}
                >
                  View All Time
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
      
      {/* Sales and Tickets Charts */}
      <Grid container spacing={3}>
        {/* Sales Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            background: 'linear-gradient(135deg, #E8F5E8 0%, #ffffff 100%)',
            border: '2px solid #00C851',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0, 200, 81, 0.2)',
            },
            transition: 'all 0.3s ease-in-out',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #00C851 0%, #4CAF50 100%)',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 200, 81, 0.3)',
                }}>
                  <Typography variant="h5" sx={{ color: 'white' }}>💰</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600" color="grey.700">
                    Sales
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {utils.formatFinancial(financialData?.total_revenue || 0)}
                  </Typography>
                </Box>
              </Box>
              {salesChart.length > 0 ? (
                <VictoryChart
                  theme={flashyTheme}
                  scale={{ x: "time" }}
                  containerComponent={<VictoryVoronoiContainer />}
                  height={300}
                >
                  <VictoryAxis
                    tickFormat={(x) => moment(x).format('M/D')}
                    style={{
                      grid: { stroke: "none" },
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickFormat={(y) => `$${y}`}
                    style={{
                      tickLabels: { fontSize: 12 },
                      grid: {
                        stroke: "rgba(162, 162, 162, 0.2)",
                        strokeDasharray: "0",
                      },
                    }}
                  />
                  <VictoryLine
                    data={salesChart}
                    x="x"
                    y="y"
                    style={{ data: { stroke: "#00C851" } }}
                    labels={({ datum }) => `$${datum.y.toFixed(2)}`}
                    labelComponent={<VictoryTooltip />}
                  />
                </VictoryChart>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="grey.500">No sales data for this period</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Tickets Sold Chart */}
        <Grid item xs={12} md={6}>
          <Card sx={{
            background: 'linear-gradient(135deg, #E8F0FF 0%, #ffffff 100%)',
            border: '2px solid #4A5BFF',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(74, 91, 255, 0.2)',
            },
            transition: 'all 0.3s ease-in-out',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                  borderRadius: '12px',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 41, 255, 0.3)',
                }}>
                  <Typography variant="h5" sx={{ color: 'white' }}>🎟️</Typography>
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600" color="grey.700">
                    Tickets Sold
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {(financialData?.total_tickets_sold || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              {ticketsChart.length > 0 ? (
                <VictoryChart
                  theme={flashyTheme}
                  scale={{ x: "time" }}
                  domainPadding={{ x: 20 }}
                  containerComponent={<VictoryVoronoiContainer />}
                  height={300}
                >
                  <VictoryAxis
                    tickFormat={(x) => moment(x).format('M/D')}
                  />
                  <VictoryAxis
                    dependentAxis
                    tickFormat={(y) => `${y}`}
                    style={{
                      tickLabels: { fontSize: 12 },
                      grid: {
                        stroke: "rgba(162, 162, 162, 0.2)",
                        strokeDasharray: "0",
                      },
                    }}
                  />
                  <VictoryBar
                    data={ticketsChart}
                    x="x"
                    y="y"
                    style={{ data: { fill: "#4A5BFF" } }}
                    labels={({ datum }) => `${datum.y}`}
                    labelComponent={<VictoryTooltip />}
                  />
                </VictoryChart>
              ) : (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="grey.500">No ticket data for this period</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
